import React, { useState } from 'react';
import {
  decidePackRounding,
  decidePackRoundingErrorMessage,
  type PackRoundingBody,
  type VendorPoPackConversion,
  type VendorPurchaseOrderDto,
} from '../../lib/vendorPurchaseOrderApi';
import { packRoundingChoices, wholePackCount } from '../../utils/packConversion';
import { positiveNumber } from '../../utils/priceUtils';

/**
 * A pending PO line in the supplier's pack (D82). The server decides everything
 * — whether a line is converted, pending or decided, and what up/down store. The
 * button figures come from the same mirrored owner the server's round endpoint
 * uses (`utils/packConversion.packRoundingChoices` / `wholePackCount`), and the
 * PO the server returns replaces what is on screen.
 */
interface PendingPackControlProps {
  poId: string;
  /** The line's index in the PO's stored `items`. */
  lineIndex: number;
  packConversion: VendorPoPackConversion;
  /** Staff may answer (edit permission, draft PO); otherwise the fact is only stated. */
  canDecide: boolean;
  onDecided: (po: VendorPurchaseOrderDto) => void;
}

export const PendingPackControl: React.FC<PendingPackControlProps> = ({
  poId,
  lineIndex,
  packConversion,
  canDecide,
  onDecided,
}) => {
  const [busy, setBusy] = useState(false);
  const [showOther, setShowOther] = useState(false);
  const [otherValue, setOtherValue] = useState('');

  const choices = packRoundingChoices(packConversion.requestedQuantity, packConversion.piecesPerPack);
  const packUnit = packConversion.packUnit || '';
  const requestedUom = packConversion.requestedUom || '';
  // What buying n packs does, in plain words: the pieces it brings and how far
  // that is over (to stock) or under (short) what the order needs. D104.33.
  const outcome = (n: number) => {
    const pieces = Math.round(n * Number(packConversion.piecesPerPack) * 1e6) / 1e6;
    const diff = Math.round((pieces - Number(packConversion.requestedQuantity)) * 1e6) / 1e6;
    const tail = diff > 0 ? `, ${diff} to stock` : diff < 0 ? `, ${-diff} short` : '';
    return `Buy ${n} ${packUnit} = ${pieces} ${requestedUom}${tail}`;
  };
  // The input is text; `positiveNumber` reads it, `wholePackCount` refuses 4.5.
  const otherPacks = wholePackCount(positiveNumber(otherValue));

  const decide = async (body: PackRoundingBody) => {
    setBusy(true);
    try {
      onDecided(await decidePackRounding(poId, lineIndex, body));
    } catch (e: unknown) {
      window.alert(decidePackRoundingErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const btn =
    'inline-flex items-center h-7 px-2.5 rounded-md border border-amber-400 bg-white text-xs font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50';

  return (
    <div className="flex flex-wrap items-center gap-2 text-[13px] text-amber-900">
      <span>
        {choices
          ? `${packConversion.requestedQuantity} ${requestedUom} needed; the supplier sells ${packUnit} of ${packConversion.piecesPerPack} (${choices.packs} ${packUnit})`
          : `This line needs a quantity in ${packUnit || 'packs'}`}
        {canDecide ? ' — buy:' : ''}
      </span>
      {canDecide && (
        <>
          {choices && (
            <button type="button" className={btn} disabled={busy} onClick={() => decide({ decision: 'up' })}>
              {outcome(choices.up)}
            </button>
          )}
          {choices && choices.down > 0 && choices.down !== choices.up && (
            <button type="button" className={btn} disabled={busy} onClick={() => decide({ decision: 'down' })}>
              {outcome(choices.down)}
            </button>
          )}
          {showOther ? (
            <span className="inline-flex items-center gap-1">
              <input
                type="number"
                min={1}
                step={1}
                value={otherValue}
                onChange={(e) => setOtherValue(e.target.value)}
                aria-label={`Number of ${packUnit || 'packs'}`}
                className="w-16 h-7 px-2 rounded border border-amber-400 bg-white text-[13px] text-slate-900"
              />
              <button
                type="button"
                className={btn}
                disabled={busy || otherPacks == null}
                onClick={() => otherPacks != null && decide({ decision: 'manual', quantity: otherPacks })}
              >
                OK
              </button>
            </span>
          ) : (
            <button type="button" className={btn} disabled={busy} onClick={() => setShowOther(true)}>
              Other…
            </button>
          )}
        </>
      )}
      {/* D119.4: where the supplier's pack came from, as the server stored it. */}
      {packConversion.sourceNote?.trim() && (
        <span className="w-full text-xs text-slate-500">Pack from: {packConversion.sourceNote.trim()}</span>
      )}
    </div>
  );
};
